import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Form from 'components/Form';
import TextField from 'components/TextField';

import { useErrorHandler } from 'lib';
import { FacebookSettings_settings$key } from '__generated__/FacebookSettings_settings.graphql';
import { FacebookSettingsMutation } from '__generated__/FacebookSettingsMutation.graphql';

type FormFields = {
  pixelId: string;
  accessToken: string;
  testCode: string;
  loginAppId: string;
  loginAppSecret: string;
};

type Props = {
  settings: FacebookSettings_settings$key;
};

const FacebookSettings: FC<Props> = props => {
  const errorHandler = useErrorHandler();

  const { facebookSettings } = useFragment(
    graphql`
      fragment FacebookSettings_settings on Settings {
        facebookSettings {
          pixelId
          accessToken
          testCode
          loginAppId
          loginAppSecret
        }
      }
    `,
    props.settings
  );

  const [update, updating] = useMutation<FacebookSettingsMutation>(graphql`
    mutation FacebookSettingsMutation($input: UpdateSettingsInput!) {
      updateSettings(input: $input) {
        ...FacebookSettings_settings
      }
    }
  `);

  const initial = {
    pixelId: facebookSettings?.pixelId ?? '',
    accessToken: facebookSettings?.accessToken ?? '',
    testCode: facebookSettings?.testCode ?? '',
    loginAppId: facebookSettings?.loginAppId ?? '',
    loginAppSecret: facebookSettings?.loginAppSecret ?? '',
  };

  const handleSubmit = useCallback(
    (facebookSettings: FormFields) => {
      update({
        variables: {
          input: { facebookSettings },
        },

        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, update]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={updating}>
        <Paper sx={{ p: 2 }}>
          <Stack spacing={4}>
            <TextField fullWidth name="pixelId" label="Pixel ID" />
            <TextField fullWidth name="accessToken" label="Access Token" />
            <TextField fullWidth name="testCode" label="Test Code" />
            <TextField fullWidth name="loginAppId" label="Login App ID" />

            <TextField
              fullWidth
              name="loginAppSecret"
              label="Login App Secret"
            />
          </Stack>
        </Paper>
      </Form>
    </Formik>
  );
};

export default FacebookSettings;
